/**
 * Colour token foundation — Path A of the frontend theme migration.
 *
 * CRELO REMAP (supersedes the rule below for hex values): the constant NAMES are still the
 * original literal names (e.g. BLUE_B3E5FC), but the 6-digit hex VALUES were remapped to the
 * Crelo palette — all blues incl. vivid cyan-blues -> indigo (only the brand info blue #0369A1 stays), greens -> success green, reds -> error red,
 * oranges -> warm amber, near-whites -> warm cream, greys (incl. cool slate greys) -> ink-tinted.
 * Lightness is preserved. To change the colour scheme again, see the re-theme guide and
 * idempotent codemod in `../hris-workspace/brand/retheme/` (RETHEME.md, retheme.mjs).
 * So a name no longer equals its value; do not "fix" values back from
 * the name. rgb()/rgba() constants were remapped the same way (alpha unchanged; white/black stay).
 *
 * ZERO VISUAL CHANGE RULE (original, pre-remap): every value below is copied verbatim from an existing
 * colour literal in react-frontend/src (see MAPPING.md for the full source audit).
 * This pass adds a name for each literal — it does NOT merge, dedupe, or "fix" any
 * value, even where two tokens are visually indistinguishable near-duplicates.
 * Merging near-duplicates is a deliberately separate, later, riskier phase — see the
 * "near-duplicate clusters" section of MAPPING.md for what that phase will need to
 * look at. Do not "clean up" values here.
 *
 * Naming rule (in priority order):
 *   1. A literal that exactly equals a PureLightTheme palette value is NOT tokenised
 *      here — use the theme reference instead (see MAPPING.md "theme.palette.*" rows).
 *   2. A literal used in a clearly semantic way (status colours, avatar ramps) would
 *      get a semantic name — none were confidently identifiable at this pass's scope
 *      (700 literals); flagged as a follow-up in the migration doc.
 *   3. Everything else is named descriptively as `<FAMILY>_<HEXDIGITS>`, e.g.
 *      `GREY_F5F5F5`, `BLUE_1976D2`, `NEAR_WHITE_FAFAFA` — ugly but honest, greppable,
 *      and it makes near-duplicate clusters visible as data instead of hiding them
 *      behind a pretty name.
 *
 * rgb()/rgba() literals follow the same rule, named `<FAMILY>_<RRGGBB>[_A<alpha%>]`.
 * When two different source literals round to the same generated name (e.g. `rgba(0,0,0,.1)`
 * vs `rgb(0 0 0 / 0.1)` — same colour, same alpha, different syntax), a numeric suffix
 * (`_2`, `_3`, …) disambiguates them. Each suffix is still a DISTINCT literal string — do not
 * assume `_2` is redundant with its un-suffixed sibling.
 *
 * Do NOT migrate call sites using this file yet — that is a separate, later pass.
 * Do NOT add typography/spacing/radius/shadow tokens here — colour only.
 */

// ---------------------------------------------------------------------------
// Near-whites / light backgrounds
// ---------------------------------------------------------------------------
export const NEAR_WHITE_ECECE7 = '#ecebe7'; // 278 occurrence(s), 49 file(s)
export const NEAR_WHITE_F7FAFC = '#f8f7fc'; // 128 occurrence(s), 9 file(s)
export const NEAR_WHITE_F8FAFC = '#f8f8fc'; // 122 occurrence(s), 30 file(s)
export const NEAR_WHITE_F5F5F5 = '#fbf6f0'; // 85 occurrence(s), 40 file(s)
export const NEAR_WHITE_FBFBF9 = '#fbfbf9'; // 80 occurrence(s), 30 file(s)
export const NEAR_WHITE_F1F5F9 = '#f2f1f9'; // 71 occurrence(s), 38 file(s)
export const NEAR_WHITE_FEE2E2 = '#fbe5e5'; // 71 occurrence(s), 36 file(s)
export const NEAR_WHITE_EEF1FF = '#f2f0fd'; // 66 occurrence(s), 22 file(s)
export const NEAR_WHITE_F3F4F6 = '#f2f2f7'; // 61 occurrence(s), 30 file(s)
export const NEAR_WHITE_E3F2FD = '#e7e5fb'; // 36 occurrence(s), 19 file(s)
export const NEAR_WHITE_FAFAF8 = '#fafaf8'; // 30 occurrence(s), 19 file(s)
export const NEAR_WHITE_FAFAFA = '#fdfaf7'; // 28 occurrence(s), 18 file(s)
export const NEAR_WHITE_F8F9FA = '#f8f8fb'; // 26 occurrence(s), 21 file(s)
export const NEAR_WHITE_E0F2FE = '#e6e3fb'; // 25 occurrence(s), 20 file(s)
export const NEAR_WHITE_FEF2F2 = '#fdf3f3'; // 19 occurrence(s), 16 file(s)
export const NEAR_WHITE_F9F9F9 = '#fcfaf6'; // 19 occurrence(s), 11 file(s)
export const NEAR_WHITE_F7F7F5 = '#f7f7f5'; // 14 occurrence(s), 3 file(s)
export const NEAR_WHITE_F0F0F0 = '#f4f0eb'; // 13 occurrence(s), 6 file(s)
export const NEAR_WHITE_F1F1F1 = '#f5f1ed'; // 12 occurrence(s), 11 file(s)
export const NEAR_WHITE_F0FDF4 = '#f1fcf5'; // 11 occurrence(s), 8 file(s)
export const NEAR_WHITE_EDF2F7 = '#eeedf7'; // 10 occurrence(s), 6 file(s)
export const NEAR_WHITE_E8F5E9 = '#e8f5ed'; // 10 occurrence(s), 5 file(s)
export const NEAR_WHITE_FFEBEE = '#fceded'; // 10 occurrence(s), 7 file(s)
export const NEAR_WHITE_F9FAFB = '#f9f9fb'; // 10 occurrence(s), 9 file(s)
export const NEAR_WHITE_EDE9FE = '#edebfc'; // 10 occurrence(s), 10 file(s)
export const NEAR_WHITE_EFF6FF = '#f2f1fd'; // 9 occurrence(s), 7 file(s)
export const NEAR_WHITE_FFFBEB = '#fefaec'; // 9 occurrence(s), 4 file(s)
export const NEAR_WHITE_F3E8FF = '#f2edfa'; // 8 occurrence(s), 6 file(s)
export const NEAR_WHITE_E1F5FE = '#e7e4fb'; // 8 occurrence(s), 6 file(s)
export const NEAR_WHITE_FFF3E0 = '#fdefe2'; // 7 occurrence(s), 7 file(s)
export const NEAR_WHITE_F0F9FF = '#f3f2fd'; // 7 occurrence(s), 7 file(s)
export const NEAR_WHITE_F1F2F4 = '#f0eff6'; // 6 occurrence(s), 6 file(s)
export const NEAR_WHITE_FFF8E1 = '#fef7e3'; // 5 occurrence(s), 2 file(s)
export const NEAR_WHITE_FFE6EB = '#fce8ef'; // 5 occurrence(s), 4 file(s)
export const NEAR_WHITE_EFF6FB = '#f0effb'; // 4 occurrence(s), 2 file(s)
export const NEAR_WHITE_ECEFF1 = '#ebeaf3'; // 4 occurrence(s), 2 file(s)
export const NEAR_WHITE_EEE = '#f3eee9'; // 4 occurrence(s), 2 file(s)
export const NEAR_WHITE_E8F5E8 = '#e8f5ed'; // 4 occurrence(s), 2 file(s)
export const NEAR_WHITE_EEF2FF = '#f2f0fd'; // 4 occurrence(s), 4 file(s)
export const NEAR_WHITE_F0F9EC = '#ecf9f1'; // 4 occurrence(s), 3 file(s)
export const NEAR_WHITE_F2F2F2 = '#f6f2ee'; // 3 occurrence(s), 3 file(s)
export const NEAR_WHITE_F0F7FF = '#f3f2fd'; // 3 occurrence(s), 2 file(s)
export const NEAR_WHITE_F8F8F6 = '#f8f8f6'; // 3 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFF7ED = '#fef5ee'; // 3 occurrence(s), 3 file(s)
export const NEAR_WHITE_E6FFFA = '#eafbf0'; // 3 occurrence(s), 1 file(s)
export const NEAR_WHITE_E8EAFF = '#edebfc'; // 3 occurrence(s), 3 file(s)
export const NEAR_WHITE_FFF4E6 = '#fdf2e8'; // 3 occurrence(s), 2 file(s)
export const NEAR_WHITE_E6F7FF = '#ebe9fc'; // 3 occurrence(s), 3 file(s)
export const NEAR_WHITE_FCFCFA = '#fcfcfa'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_EDE7F6 = '#ede7f6'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_FCE4EC = '#fce4ec'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_F0F2F5 = '#f0eff6'; // 2 occurrence(s), 1 file(s)
export const NEAR_WHITE_FBE9E7 = '#fbe7e7'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_E8F0FF = '#edebfc'; // 2 occurrence(s), 1 file(s)
export const NEAR_WHITE_E8E8E8 = '#efe8e1'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_FFF3F3 = '#fef4f4'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_EBEBEB = '#f1ebe5'; // 2 occurrence(s), 1 file(s)
export const NEAR_WHITE_F1F1EC = '#f1f0ec'; // 2 occurrence(s), 1 file(s)
export const NEAR_WHITE_E6F3FF = '#ebe9fc'; // 2 occurrence(s), 1 file(s)
export const NEAR_WHITE_ECFDF5 = '#eefbf3'; // 2 occurrence(s), 1 file(s)
export const NEAR_WHITE_F0F4FF = '#f3f2fd'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_F6F6F6 = '#fbf7f1'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_EEF0F2 = '#edecf4'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FAFBFC = '#fafafc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FCFDFE = '#fcfcfe'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_E6F2FF = '#ebe9fc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFF1F0 = '#fdf2f2'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_F8FAFB = '#f8f8fb'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_E5F7FF = '#ebe8fc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_E4E8FF = '#eae7fc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FAFAFB = '#faf9fc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FAFAFC = '#fafafc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_F0DEFD = '#ebe3f8'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_DEF4FF = '#e5e2fb'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_EDEDED = '#f2ede8'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_F8F8F8 = '#fcf9f4'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFE1E5 = '#fbe5e5'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_E1FFE9 = '#e6faed'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_F8F9FB = '#f8f8fb'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFDE7 = '#fef8e8'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FAE8FF = '#f2edfa'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_E0E7FF = '#e7e4fb'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FAFAF7 = '#faf9f7'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFCFC = '#fffcfc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FDECEA = '#fcebeb'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFF4E0 = '#fdefe2'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_E8EEFC = '#ebe8fc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_F3E8FD = '#f1ebfa'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FDE8E8 = '#fce9e9'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FAF5FF = '#f9f7fd'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_F5F3FF = '#f6f4fe'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FAFFFA = '#fbfefc'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFF5F5 = '#fef6f6'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A50 = 'rgba(255, 255, 255, 0.5)'; // 12 occurrence(s), 12 file(s)
export const NEAR_WHITE_FFFFFF = 'rgb(255, 255, 255)'; // 10 occurrence(s), 10 file(s)
// Plain hex white. theme.palette.common.white is value-identical, but some
// call sites are module-scope helpers with no hook access to `theme` — this
// exists for exactly those.
export const WHITE_FFFFFF = '#ffffff';
export const NEAR_WHITE_FFFFFF_A80 = 'rgba(255, 255, 255, 0.8)'; // 9 occurrence(s), 7 file(s)
export const NEAR_WHITE_FFFFFF_A30 = 'rgba(255, 255, 255, 0.3)'; // 6 occurrence(s), 3 file(s)
export const NEAR_WHITE_FFFFFF_A87 = 'rgba(255, 255, 255, 0.87)'; // 3 occurrence(s), 2 file(s)
export const NEAR_WHITE_E5E8FF = 'rgb(235, 232, 252)'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_FFFFFF_A20 = 'rgba(255, 255, 255, 0.2)'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_FFFFFF_A70 = 'rgba(255, 255, 255, 0.7)'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_FFFFFF_A40 = 'rgba(255, 255, 255, 0.4)'; // 2 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A10 = 'rgba(255, 255, 255, 0.1)'; // 2 occurrence(s), 2 file(s)
export const NEAR_WHITE_FFFFFF_A95 = 'rgba(255, 255, 255, 0.95)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_F8FAFC_A30 = 'rgba(248, 248, 252, 0.3)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A90 = 'rgba(255, 255, 255, 0.9)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A30_2 = 'rgb(255 255 255 / 30%)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A18 = 'rgba(255, 255, 255, 0.18)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A7 = 'rgba(255, 255, 255, 0.07)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A15 = 'rgba(255, 255, 255, 0.15)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A55 = 'rgba(255, 255, 255, 0.55)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A18_2 = 'rgba(255, 255, 255, .18)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A14 = 'rgba(255, 255, 255, .14)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A85 = 'rgba(255, 255, 255, .85)'; // 1 occurrence(s), 1 file(s)
export const NEAR_WHITE_FFFFFF_A70_2 = 'rgba(255, 255, 255, .7)'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Greys
// ---------------------------------------------------------------------------
export const GREY_6B6B72 = '#696677'; // 437 occurrence(s), 51 file(s)
export const GREY_9B9BA1 = '#9996a6'; // 135 occurrence(s), 40 file(s)
export const GREY_6B7280 = '#6f6c7f'; // 130 occurrence(s), 51 file(s)
export const GREY_2A2A2E = '#2a2830'; // 111 occurrence(s), 34 file(s)
export const GREY_DCDCD5 = '#d6d5dc'; // 92 occurrence(s), 30 file(s)
export const GREY_333 = '#302f37'; // 74 occurrence(s), 20 file(s)
export const GREY_E0E0E0 = '#dedee2'; // 59 occurrence(s), 27 file(s)
export const GREY_666 = '#615e6e'; // 44 occurrence(s), 15 file(s)
export const GREY_DDD = '#dbdae0'; // 43 occurrence(s), 17 file(s)
export const GREY_9CA3AF = '#a09dae'; // 37 occurrence(s), 21 file(s)
export const GREY_757575 = '#6f6c7e'; // 22 occurrence(s), 15 file(s)
export const GREY_9E9E9E = '#9996a6'; // 21 occurrence(s), 14 file(s)
export const GREY_CCC = '#c9c8d0'; // 18 occurrence(s), 12 file(s)
export const GREY_7F7D7D = '#777488'; // 15 occurrence(s), 1 file(s)
export const GREY_C1C1C1 = '#bebcc6'; // 6 occurrence(s), 5 file(s)
export const GREY_A8A8A8 = '#a3a1af'; // 6 occurrence(s), 5 file(s)
export const GREY_555 = '#504e5c'; // 6 occurrence(s), 6 file(s)
export const GREY_495057 = '#4c4a56'; // 6 occurrence(s), 3 file(s)
export const GREY_999 = '#9491a1'; // 6 occurrence(s), 4 file(s)
export const GREY_E1DEDE91 = '#dedde291'; // 5 occurrence(s), 2 file(s)
export const GREY_818078 = '#767386'; // 4 occurrence(s), 2 file(s)
export const GREY_5C5454 = '#53515f'; // 4 occurrence(s), 1 file(s)
export const GREY_5E5C5C = '#585664'; // 4 occurrence(s), 4 file(s)
export const GREY_3A3A3F = '#393841'; // 4 occurrence(s), 2 file(s)
export const GREY_C8CFD0 = '#c9c8d0'; // 4 occurrence(s), 2 file(s)
export const GREY_888 = '#827e92'; // 3 occurrence(s), 3 file(s)
export const GREY_E2E3E5 = '#e2e1e6'; // 3 occurrence(s), 2 file(s)
export const GREY_868D9B = '#8b8899'; // 3 occurrence(s), 2 file(s)
export const GREY_E1E1E1 = '#dfdfe3'; // 2 occurrence(s), 1 file(s)
export const GREY_BDBDBD = '#b9b8c2'; // 2 occurrence(s), 2 file(s)
export const GREY_6A737D = '#6d6a7d'; // 2 occurrence(s), 1 file(s)
export const GREY_5F6368 = '#5e5c6b'; // 2 occurrence(s), 2 file(s)
export const GREY_8A917F8C = '#827e928c'; // 2 occurrence(s), 1 file(s)
export const GREY_33363B = '#34333b'; // 2 occurrence(s), 2 file(s)
export const GREY_C3C3C1 = '#bfbdc7'; // 2 occurrence(s), 1 file(s)
export const GREY_524C4D = '#4b4955'; // 2 occurrence(s), 1 file(s)
export const GREY_D0D0D0 = '#cdccd4'; // 2 occurrence(s), 2 file(s)
export const GREY_383D41 = '#393841'; // 2 occurrence(s), 1 file(s)
export const GREY_B0B0B0 = '#acaab6'; // 2 occurrence(s), 1 file(s)
export const GREY_7474774D = '#6f6c7f4d'; // 2 occurrence(s), 2 file(s)
export const GREY_A2A2A2 = '#9d9ba9'; // 1 occurrence(s), 1 file(s)
export const GREY_ADB5BD = '#b0aebc'; // 1 occurrence(s), 1 file(s)
export const GREY_8C9196 = '#8b889a'; // 1 occurrence(s), 1 file(s)
export const GREY_434343 = '#3f3e48'; // 1 occurrence(s), 1 file(s)
export const GREY_D6D6D6 = '#d4d3d9'; // 1 occurrence(s), 1 file(s)
export const GREY_3C4142 = '#3c3a44'; // 1 occurrence(s), 1 file(s)
export const GREY_8E8E9C = '#8f8d9d'; // 1 occurrence(s), 1 file(s)
export const GREY_6C757D = '#6e6b7e'; // 1 occurrence(s), 1 file(s)
export const GREY_9C9992 = '#918f9f'; // 1 occurrence(s), 1 file(s)
export const GREY_BBB = '#b7b6c0'; // 1 occurrence(s), 1 file(s)
export const GREY_AAA = '#a5a3b1'; // 1 occurrence(s), 1 file(s)
export const GREY_D3D3D3 = '#d1cfd7'; // 1 occurrence(s), 1 file(s)
export const GREY_696969 = '#636171'; // 1 occurrence(s), 1 file(s)
export const GREY_6D6D74 = '#6b687a'; // 1 occurrence(s), 1 file(s)
export const GREY_616161 = '#5c5969'; // 1 occurrence(s), 1 file(s)
export const GREY_C7C7CC = '#c7c5ce'; // 1 occurrence(s), 1 file(s)
export const GREY_D9D9DF66 = '#dad9df66'; // 1 occurrence(s), 1 file(s)
export const GREY_333333 = '#302f37'; // 1 occurrence(s), 1 file(s)
export const GREY_C2C1C1 = 'rgb(190, 189, 198)'; // 10 occurrence(s), 10 file(s)
export const GREY_E0E0E0_A100 = 'rgba(222, 222, 226, 1)'; // 5 occurrence(s), 3 file(s)
export const GREY_474746 = 'rgb(67 65 76)'; // 4 occurrence(s), 2 file(s)
export const GREY_747070_A80 = 'rgba(108, 105, 123, 0.8)'; // 3 occurrence(s), 1 file(s)
export const GREY_C6CDCB_A36 = 'rgba(199, 197, 206, 0.36)'; // 2 occurrence(s), 1 file(s)
export const GREY_9CA3AF_A8 = 'rgba(160, 157, 174, 0.08)'; // 2 occurrence(s), 1 file(s)
export const GREY_9CA3AF_A16 = 'rgba(160, 157, 174, 0.16)'; // 1 occurrence(s), 1 file(s)
export const GREY_9CA3AF_A10 = 'rgba(160, 157, 174, 0.1)'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Near-blacks
// ---------------------------------------------------------------------------
export const NEAR_BLACK_0A0A0B = '#0a0a0b'; // 259 occurrence(s), 45 file(s)
export const NEAR_BLACK_000 = '#000'; // 25 occurrence(s), 12 file(s)
export const NEAR_BLACK_000F1DFF = '#080716ff'; // 10 occurrence(s), 6 file(s)
export const NEAR_BLACK_000000 = '#000000'; // 5 occurrence(s), 4 file(s)
export const NEAR_BLACK_111 = '#101012'; // 4 occurrence(s), 2 file(s)
export const NEAR_BLACK_222 = '#201f25'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_1F1F22 = '#1f1e23'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_1A1A1A = '#19181c'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_212529 = '#232129'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A6 = 'rgba(0, 0, 0, 0.06)'; // 46 occurrence(s), 21 file(s)
export const NEAR_BLACK_000000_A10 = 'rgba(0, 0, 0, 0.1)'; // 44 occurrence(s), 26 file(s)
export const NEAR_BLACK_000000_A10_2 = 'rgb(0 0 0 / 0.1)'; // 33 occurrence(s), 14 file(s)
export const NEAR_BLACK_000000_A8 = 'rgba(0, 0, 0, 0.08)'; // 21 occurrence(s), 15 file(s)
export const NEAR_BLACK_000000_A15 = 'rgba(0, 0, 0, 0.15)'; // 21 occurrence(s), 16 file(s)
export const NEAR_BLACK_000000_A5 = 'rgba(0, 0, 0, 0.05)'; // 12 occurrence(s), 8 file(s)
export const NEAR_BLACK_0A0A0B_A4 = 'rgba(10, 10, 11, .04)'; // 12 occurrence(s), 11 file(s)
export const NEAR_BLACK_000000_A20 = 'rgba(0, 0, 0, 0.2)'; // 11 occurrence(s), 9 file(s)
export const NEAR_BLACK_000000_A12 = 'rgba(0, 0, 0, 0.12)'; // 10 occurrence(s), 9 file(s)
export const NEAR_BLACK_000000_A4 = 'rgba(0, 0, 0, .04)'; // 10 occurrence(s), 5 file(s)
export const NEAR_BLACK_000000_A4_2 = 'rgba(0, 0, 0, 0.04)'; // 7 occurrence(s), 6 file(s)
export const NEAR_BLACK_0A0A0B_A4_2 = 'rgba(10, 10, 11, 0.04)'; // 7 occurrence(s), 4 file(s)
export const NEAR_BLACK_0A0A0B_A3 = 'rgba(10, 10, 11, .03)'; // 6 occurrence(s), 6 file(s)
export const NEAR_BLACK_000000_A23 = 'rgba(0, 0, 0, 0.23)'; // 4 occurrence(s), 3 file(s)
export const NEAR_BLACK_0A0A0B_A3_2 = 'rgba(10, 10, 11, 0.03)'; // 4 occurrence(s), 4 file(s)
export const NEAR_BLACK_000000_A42 = 'rgba(0, 0, 0, 0.42)'; // 4 occurrence(s), 4 file(s)
export const NEAR_BLACK_000000_A30 = 'rgba(0, 0, 0, 0.3)'; // 3 occurrence(s), 2 file(s)
export const NEAR_BLACK_0A0A0B_A6 = 'rgba(10, 10, 11, 0.06)'; // 3 occurrence(s), 3 file(s)
export const NEAR_BLACK_000000_A2 = 'rgba(0, 0, 0, 0.02)'; // 3 occurrence(s), 3 file(s)
export const NEAR_BLACK_000000_A60 = 'rgba(0, 0, 0, 0.6)'; // 3 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A40 = 'rgba(0, 0, 0, 0.4)'; // 3 occurrence(s), 3 file(s)
export const NEAR_BLACK_0A0A0B_A6_2 = 'rgba(10, 10, 11, .06)'; // 3 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A22 = 'rgba(0, 0, 0, .22)'; // 3 occurrence(s), 3 file(s)
export const NEAR_BLACK_000000_A5_2 = 'rgba(0, 0, 0, .05)'; // 3 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A10_3 = 'rgba(0, 0, 0, .1)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A35 = 'rgba(0, 0, 0, 0.35)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_0A0A0B_A12 = 'rgba(10, 10, 11, .12)'; // 2 occurrence(s), 1 file(s)
export const NEAR_BLACK_0A0A0B_A18 = 'rgba(10, 10, 11, .18)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A8_2 = 'rgba(0, 0, 0, .08)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A25 = 'rgba(0, 0, 0, 0.25)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_0A0A0B_A32 = 'rgba(10, 10, 11, .32)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_0A0A0B_A5 = 'rgba(10, 10, 11, 0.05)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A2_2 = 'rgba(0, 0, 0, .015)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A90 = 'rgba(0, 0, 0, .9)'; // 2 occurrence(s), 2 file(s)
export const NEAR_BLACK_000000_A60_2 = 'rgb(0 0 0 / 60%)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A45 = 'rgb(0 0 0 / 45%)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_070919_A18 = 'rgba(9, 7, 25, .18)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_070919_A15 = 'rgba(9, 7, 25, .15)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A12_2 = 'rgba(0, 0, 0, .12)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A54 = 'rgba(0, 0, 0, 0.54)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_0A0A0B_A35 = 'rgba(10, 10, 11, .35)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A2_3 = 'rgba(0, 0, 0, .016)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_14141E_A5 = 'rgba(23, 22, 27, .05)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_14141E_A3 = 'rgba(23, 22, 27, .03)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A7 = 'rgba(0, 0, 0, 0.07)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_0A0A0B_A18_2 = 'rgba(10, 10, 11, 0.18)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A50 = 'rgba(0, 0, 0, 0.5)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A55 = 'rgba(0, 0, 0, 0.55)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A6_2 = 'rgba(0, 0, 0, .06)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A24 = 'rgba(0, 0, 0, 0.24)'; // 1 occurrence(s), 1 file(s)
export const NEAR_BLACK_000000_A14 = 'rgba(0, 0, 0, 0.14)'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Blues
// ---------------------------------------------------------------------------
export const BLUE_E2E8F0 = '#e4e2f0'; // 338 occurrence(s), 26 file(s)
export const BLUE_4A5568 = '#535062'; // 181 occurrence(s), 15 file(s)
export const BLUE_1F4FFF = '#6051cd'; // 129 occurrence(s), 36 file(s)
export const BLUE_1976D2 = '#4435b6'; // 123 occurrence(s), 42 file(s)
export const BLUE_374151 = '#3f3d4b'; // 93 occurrence(s), 15 file(s)
export const BLUE_2D3748 = '#373540'; // 85 occurrence(s), 7 file(s)
export const BLUE_475569 = '#524f61'; // 72 occurrence(s), 31 file(s)
export const BLUE_667EEA = '#8378d8'; // 53 occurrence(s), 13 file(s)
export const BLUE_E5E7EB = '#e4e2ee'; // 29 occurrence(s), 19 file(s)
export const BLUE_64748B = '#706c83'; // 28 occurrence(s), 14 file(s)
export const BLUE_5A6FD8 = '#6e61d1'; // 28 occurrence(s), 10 file(s)
export const BLUE_1A202C = '#212027'; // 26 occurrence(s), 10 file(s)
export const BLUE_D1D5DB = '#d3d2da'; // 26 occurrence(s), 21 file(s)
export const BLUE_00A8E8 = '#4334b4'; // 22 occurrence(s), 18 file(s)
export const BLUE_0EA5E9 = '#4738bf'; // 22 occurrence(s), 18 file(s)
export const BLUE_1E293B = '#211e3b'; // 21 occurrence(s), 9 file(s)
export const BLUE_1E40AF = '#3b2e9f'; // 21 occurrence(s), 11 file(s)
export const BLUE_DBEAFE = '#e2dffa'; // 20 occurrence(s), 11 file(s)
export const BLUE_81D4FA = '#a299e2'; // 17 occurrence(s), 6 file(s)
export const BLUE_2196F3 = '#594aca'; // 17 occurrence(s), 12 file(s)
export const BLUE_6366F1 = '#867bd9'; // 17 occurrence(s), 12 file(s)
export const BLUE_B3E5FC = '#c7c2ed'; // 16 occurrence(s), 5 file(s)
export const BLUE_3B82F6 = '#6d60d1'; // 16 occurrence(s), 12 file(s)
export const BLUE_94A3B8 = '#a09daf'; // 15 occurrence(s), 14 file(s)
export const BLUE_111827 = '#141127'; // 15 occurrence(s), 6 file(s)
export const BLUE_2980B9 = '#4133af'; // 15 occurrence(s), 6 file(s)
export const BLUE_3F51B5 = '#4d3fb5'; // 15 occurrence(s), 12 file(s)
export const BLUE_42A5F5 = '#7265d2'; // 14 occurrence(s), 10 file(s)
export const BLUE_1565C0 = '#3e30a5'; // 14 occurrence(s), 9 file(s)
export const BLUE_0277BD = '#372b94'; // 14 occurrence(s), 6 file(s)
export const BLUE_1D4ED8 = '#4737be'; // 13 occurrence(s), 10 file(s)
export const BLUE_BBDEFB = '#c6c0f6'; // 12 occurrence(s), 8 file(s)
export const BLUE_718096 = '#7b7790'; // 11 occurrence(s), 5 file(s)
export const BLUE_4F46E5 = '#695bd0'; // 8 occurrence(s), 6 file(s)
export const BLUE_223354B3 = '#282254b3'; // 8 occurrence(s), 5 file(s)
export const BLUE_334155 = '#3f3d4b'; // 7 occurrence(s), 5 file(s)
export const BLUE_CBD5E1 = '#d3d2da'; // 7 occurrence(s), 4 file(s)
export const BLUE_4FC3F7 = '#7c70d6'; // 7 occurrence(s), 5 file(s)
export const BLUE_9ED0F0 = '#afa8e6'; // 7 occurrence(s), 1 file(s)
export const BLUE_607D8B = '#6e6a81'; // 7 occurrence(s), 5 file(s)
export const BLUE_29D = '#4a39c6'; // 6 occurrence(s), 2 file(s)
export const BLUE_1E3A8A = '#312682'; // 6 occurrence(s), 5 file(s)
export const BLUE_90CAF9 = '#aca4e5'; // 6 occurrence(s), 4 file(s)
export const BLUE_2563EB = '#5647c9'; // 6 occurrence(s), 5 file(s)
export const BLUE_E9ECEF = '#e8e7f1'; // 5 occurrence(s), 4 file(s)
export const BLUE_3D4ECC = '#5141c8'; // 5 occurrence(s), 4 file(s)
export const BLUE_BFDBFE = '#ccc6f7'; // 5 occurrence(s), 4 file(s)
export const BLUE_123 = '#151133'; // 5 occurrence(s), 1 file(s)
export const BLUE_125 = '#1d174f'; // 5 occurrence(s), 1 file(s)
export const BLUE_4481EB = '#6c5fd0'; // 4 occurrence(s), 1 file(s)
export const BLUE_8884D8 = '#8e84d8'; // 4 occurrence(s), 1 file(s)
export const BLUE_0B649C = '#302681'; // 4 occurrence(s), 1 file(s)
export const BLUE_455A64 = '#4f4c5d'; // 3 occurrence(s), 1 file(s)
export const BLUE_2D3CA7 = '#3d30a4'; // 3 occurrence(s), 3 file(s)
export const BLUE_0369A1 = '#2f257f'; // 3 occurrence(s), 2 file(s)
export const BLUE_1A3FCC = '#4334b2'; // 3 occurrence(s), 1 file(s)
export const BLUE_0284C7 = '#3a2d9c'; // 3 occurrence(s), 3 file(s)
export const BLUE_1EA7E0 = '#4939c5'; // 3 occurrence(s), 2 file(s)
export const BLUE_007BFF = '#4a39c6'; // 2 occurrence(s), 1 file(s)
export const BLUE_1C81C2 = '#4032ac'; // 2 occurrence(s), 2 file(s)
export const BLUE_5C6AC0 = '#685cc0'; // 2 occurrence(s), 2 file(s)
export const BLUE_D0E2FF = '#dad6f9'; // 2 occurrence(s), 1 file(s)
export const BLUE_1ABCFE = '#5c4dcb'; // 2 occurrence(s), 2 file(s)
export const BLUE_D6F0FF = '#dfdbfa'; // 2 occurrence(s), 2 file(s)
export const BLUE_01579B = '#2d2379'; // 2 occurrence(s), 1 file(s)
export const BLUE_115293 = '#2f257f'; // 2 occurrence(s), 2 file(s)
export const BLUE_17BCFF = '#5a4bcb'; // 2 occurrence(s), 2 file(s)
export const BLUE_1740CC = '#4233b0'; // 2 occurrence(s), 2 file(s)
export const BLUE_4338CA = '#4c3cc6'; // 2 occurrence(s), 2 file(s)
export const BLUE_1E90FF = '#5f51cc'; // 2 occurrence(s), 2 file(s)
export const BLUE_8ECBEB = '#a098e1'; // 2 occurrence(s), 2 file(s)
export const BLUE_637381 = '#6a677d'; // 2 occurrence(s), 1 file(s)
export const BLUE_3182CE = '#4a39c6'; // 2 occurrence(s), 1 file(s)
export const BLUE_BEE3F8 = '#c6c0f6'; // 2 occurrence(s), 1 file(s)
export const BLUE_A0AEC0 = '#aba8b8'; // 2 occurrence(s), 1 file(s)
export const BLUE_1F2937 = '#28272f'; // 2 occurrence(s), 2 file(s)
export const BLUE_93C5FD = '#b1aae6'; // 2 occurrence(s), 2 file(s)
export const BLUE_3B5BDB = '#5a4bcb'; // 1 occurrence(s), 1 file(s)
export const BLUE_4C6EF5 = '#796dd4'; // 1 occurrence(s), 1 file(s)
export const BLUE_698FB7 = '#7269b7'; // 1 occurrence(s), 1 file(s)
export const BLUE_0077B5 = '#34298c'; // 1 occurrence(s), 1 file(s)
export const BLUE_04BEFE = '#4c3cc6'; // 1 occurrence(s), 1 file(s)
export const BLUE_1A1F36 = '#1d1a36'; // 1 occurrence(s), 1 file(s)
export const BLUE_0070F3 = '#4637bc'; // 1 occurrence(s), 1 file(s)
export const BLUE_E0E4E8 = '#e2e1e7'; // 1 occurrence(s), 1 file(s)
export const BLUE_EAEDF0 = '#eae9f2'; // 1 occurrence(s), 1 file(s)
export const BLUE_DEE2E6 = '#e0dfe5'; // 1 occurrence(s), 1 file(s)
export const BLUE_6B73FF = '#968cde'; // 1 occurrence(s), 1 file(s)
export const BLUE_000DFF = '#4a39c6'; // 1 occurrence(s), 1 file(s)
export const BLUE_ABDCFF = '#c3beec'; // 1 occurrence(s), 1 file(s)
export const BLUE_0396FF = '#4c3cc6'; // 1 occurrence(s), 1 file(s)
export const BLUE_141E30 = '#171430'; // 1 occurrence(s), 1 file(s)
export const BLUE_243B55 = '#2a2455'; // 1 occurrence(s), 1 file(s)
export const BLUE_2B5876 = '#342b76'; // 1 occurrence(s), 1 file(s)
export const BLUE_4E4376 = '#565366'; // 1 occurrence(s), 1 file(s)
export const BLUE_97ABFF = '#b5aee8'; // 1 occurrence(s), 1 file(s)
export const BLUE_123597 = '#312683'; // 1 occurrence(s), 1 file(s)
export const BLUE_43CBFF = '#796dd5'; // 1 occurrence(s), 1 file(s)
export const BLUE_29323C = '#2f2d38'; // 1 occurrence(s), 1 file(s)
export const BLUE_485563 = '#504d5e'; // 1 occurrence(s), 1 file(s)
export const BLUE_4B5563 = '#514e60'; // 1 occurrence(s), 1 file(s)
export const BLUE_DFEBF6 = '#e2dff6'; // 1 occurrence(s), 1 file(s)
export const BLUE_E9E9EE = '#e8e7f0'; // 1 occurrence(s), 1 file(s)
export const BLUE_1C1C2E = '#232129'; // 1 occurrence(s), 1 file(s)
export const BLUE_CFE2FF = '#d9d5f9'; // 1 occurrence(s), 1 file(s)
export const BLUE_084298 = '#2e247c'; // 1 occurrence(s), 1 file(s)
export const BLUE_64B5F6 = '#8a80da'; // 1 occurrence(s), 1 file(s)
export const BLUE_1E88E5 = '#4d3cc7'; // 1 occurrence(s), 1 file(s)
export const BLUE_121826 = '#141226'; // 1 occurrence(s), 1 file(s)
export const BLUE_DDE1E7 = '#e0dfe5'; // 1 occurrence(s), 1 file(s)
export const BLUE_153BBB = '#3c2fa1'; // 1 occurrence(s), 1 file(s)
export const BLUE_218 = '#2c2277'; // 1 occurrence(s), 1 file(s)
export const BLUE_D1E6FF = '#dbd7f9'; // 1 occurrence(s), 1 file(s)
export const BLUE_039 = '#2c2277'; // 1 occurrence(s), 1 file(s)
export const BLUE_227 = '#2c2277'; // 1 occurrence(s), 1 file(s)
export const BLUE_87CEEB = '#9b93e0'; // 1 occurrence(s), 1 file(s)
export const BLUE_191970 = '#281f6a'; // 1 occurrence(s), 1 file(s)
export const BLUE_0C4A6E = '#231b5f'; // 1 occurrence(s), 1 file(s)
export const BLUE_5966FF = '#897eda'; // 1 occurrence(s), 1 file(s)
export const BLUE_8ED4F3 = '#a69ee3'; // 1 occurrence(s), 1 file(s)
export const BLUE_7DC0DF = '#8c81db'; // 1 occurrence(s), 1 file(s)
export const BLUE_1A1A2E = '#222028'; // 1 occurrence(s), 1 file(s)
export const BLUE_16213E = '#1b163e'; // 1 occurrence(s), 1 file(s)
export const BLUE_0F3460 = '#201956'; // 1 occurrence(s), 1 file(s)
export const BLUE_157 = '#271f69'; // 1 occurrence(s), 1 file(s)
export const BLUE_1A3FA0 = '#362a90'; // 1 occurrence(s), 1 file(s)
export const BLUE_0D47A1 = '#322787'; // 1 occurrence(s), 1 file(s)
export const BLUE_127 = '#271f69'; // 1 occurrence(s), 1 file(s)
export const BLUE_90A4AE = '#9995a9'; // 1 occurrence(s), 1 file(s)
export const BLUE_4052CC = '#5343c9'; // 1 occurrence(s), 1 file(s)
export const BLUE_5568D3 = '#6759cf'; // 1 occurrence(s), 1 file(s)
export const BLUE_223354_A10 = 'rgba(40, 34, 84, 0.1)'; // 17 occurrence(s), 16 file(s)
export const BLUE_223354_A5 = 'rgba(40, 34, 84, 0.05)'; // 17 occurrence(s), 16 file(s)
export const BLUE_223354_A70 = 'rgba(40, 34, 84, 0.7)'; // 13 occurrence(s), 9 file(s)
export const BLUE_1976D2_A8 = 'rgba(68, 53, 182, 0.08)'; // 10 occurrence(s), 7 file(s)
export const BLUE_0076FF_A39 = 'rgba(74, 57, 198, 0.39)'; // 7 occurrence(s), 7 file(s)
export const BLUE_1976D2_A20 = 'rgba(68, 53, 182, 0.2)'; // 6 occurrence(s), 3 file(s)
export const BLUE_1976D2_A4 = 'rgba(68, 53, 182, 0.04)'; // 5 occurrence(s), 5 file(s)
export const BLUE_1976D2_A16 = 'rgba(68, 53, 182, 0.16)'; // 3 occurrence(s), 3 file(s)
export const BLUE_26539C = 'rgb(56, 44, 150)'; // 3 occurrence(s), 3 file(s)
export const BLUE_9FA2BF_A18 = 'rgba(170, 167, 183, .18)'; // 3 occurrence(s), 1 file(s)
export const BLUE_9FA2BF_A32 = 'rgba(170, 167, 183, 0.32)'; // 3 occurrence(s), 1 file(s)
export const BLUE_DAF4FF_A75 = 'rgba(226, 223, 250, 0.75)'; // 3 occurrence(s), 3 file(s)
export const BLUE_9FA2BF_A18_2 = 'rgb(170 167 183 / 18%)'; // 3 occurrence(s), 2 file(s)
export const BLUE_9FA2BF_A32_2 = 'rgb(170 167 183 / 32%)'; // 3 occurrence(s), 2 file(s)
export const BLUE_1976D2_A10 = 'rgba(68, 53, 182, 0.1)'; // 3 occurrence(s), 3 file(s)
export const BLUE_919EAB_A24 = 'rgba(152, 148, 168, 0.24)'; // 3 occurrence(s), 1 file(s)
export const BLUE_1976D2_A30 = 'rgba(68, 53, 182, 0.3)'; // 2 occurrence(s), 2 file(s)
export const BLUE_1F4FFF_A25 = 'rgba(96, 81, 205, .25)'; // 2 occurrence(s), 1 file(s)
export const BLUE_6366F1_A30 = 'rgba(134, 123, 217, 0.3)'; // 2 occurrence(s), 2 file(s)
export const BLUE_1F4FFF_A2 = 'rgba(96, 81, 205, 0.02)'; // 2 occurrence(s), 1 file(s)
export const BLUE_2563EB_A25 = 'rgba(86, 71, 201, 0.25)'; // 1 occurrence(s), 1 file(s)
export const BLUE_2563EB_A40 = 'rgba(86, 71, 201, 0.4)'; // 1 occurrence(s), 1 file(s)
export const BLUE_4481EB_A8 = 'rgba(108, 95, 208, 0.08)'; // 1 occurrence(s), 1 file(s)
export const BLUE_33C2FF_A25 = 'rgba(110, 97, 209, 0.25)'; // 1 occurrence(s), 1 file(s)
export const BLUE_33C2FF_A35 = 'rgba(110, 97, 209, 0.35)'; // 1 occurrence(s), 1 file(s)
export const BLUE_5569FF_A25 = 'rgba(134, 123, 217, 0.25)'; // 1 occurrence(s), 1 file(s)
export const BLUE_5569FF_A35 = 'rgba(134, 123, 217, 0.35)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1F4FFF_A12 = 'rgba(96, 81, 205, 0.12)'; // 1 occurrence(s), 1 file(s)
export const BLUE_2196F3_A10 = 'rgba(89, 74, 202, 0.1)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1F4FFF_A4 = 'rgba(96, 81, 205, .04)'; // 1 occurrence(s), 1 file(s)
export const BLUE_5569FF_A15 = 'rgba(134, 123, 217, 0.15)'; // 1 occurrence(s), 1 file(s)
export const BLUE_5569FF_A5 = 'rgba(134, 123, 217, 0.05)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1976D2_A12 = 'rgba(68, 53, 182, 0.12)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1F4FFF_A15 = 'rgba(96, 81, 205, .15)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1E293B_A100 = 'rgba(33, 30, 59, 1)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1976D2_A25 = 'rgba(68, 53, 182, 0.25)'; // 1 occurrence(s), 1 file(s)
export const BLUE_919EAB_A12 = 'rgba(152, 148, 168, 0.12)'; // 1 occurrence(s), 1 file(s)
export const BLUE_919EAB_A8 = 'rgba(152, 148, 168, 0.08)'; // 1 occurrence(s), 1 file(s)
export const BLUE_6366F1_A40 = 'rgba(134, 123, 217, 0.4)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1E293B_A70 = 'rgba(33, 30, 59, 0.7)'; // 1 occurrence(s), 1 file(s)
export const BLUE_1F4FFF_A20 = 'rgba(96, 81, 205, 0.2)'; // 1 occurrence(s), 1 file(s)
export const BLUE_6366F1_A8 = 'rgba(134, 123, 217, 0.08)'; // 1 occurrence(s), 1 file(s)
export const BLUE_6366F1_A16 = 'rgba(134, 123, 217, 0.16)'; // 1 occurrence(s), 1 file(s)
export const BLUE_6366F1_A20 = 'rgba(134, 123, 217, 0.2)'; // 1 occurrence(s), 1 file(s)
export const BLUE_223354_A50 = 'rgba(40, 34, 84, 0.5)'; // 1 occurrence(s), 1 file(s)
export const BLUE_5569FF_A30 = 'rgb(134 123 217 / 0.3)'; // 1 occurrence(s), 1 file(s)
export const BLUE_5569FF_A40 = 'rgb(134 123 217 / 0.4)'; // 1 occurrence(s), 1 file(s)
// Solid forms of the two brand colours, for plain-CSS call sites (Crelo Indigo / Crelo ink).
export const BLUE_5569FF = '#5547c8'; // 17 occurrence(s), 8 file(s)
export const BLUE_223354 = '#221e3f'; // 10 occurrence(s), 4 file(s)

// ---------------------------------------------------------------------------
// Cyans / teals
// ---------------------------------------------------------------------------
export const CYAN_06B6D4 = '#3f31a9'; // 8 occurrence(s), 8 file(s)
export const CYAN_155E75 = '#281f6b'; // 7 occurrence(s), 6 file(s)
export const CYAN_CFFAFE = '#d8d4f9'; // 6 occurrence(s), 5 file(s)
export const CYAN_35CCE6 = '#5e4fcc'; // 6 occurrence(s), 5 file(s)
export const CYAN_0891B2 = '#362a90'; // 5 occurrence(s), 5 file(s)
export const CYAN_B2EBF2 = '#bfb9eb'; // 5 occurrence(s), 3 file(s)
export const CYAN_0F766E = '#1b6a56'; // 4 occurrence(s), 4 file(s)
export const CYAN_D1ECF1 = '#d5d1f1'; // 4 occurrence(s), 3 file(s)
export const CYAN_0C5460 = '#1f1854'; // 4 occurrence(s), 3 file(s)
export const CYAN_009688 = '#1e7862'; // 4 occurrence(s), 4 file(s)
export const CYAN_64FFDA = '#7fe4a4'; // 4 occurrence(s), 1 file(s)
export const CYAN_17A2B8 = '#3c2fa0'; // 3 occurrence(s), 2 file(s)
export const CYAN_38B2AC = '#38b293'; // 3 occurrence(s), 1 file(s)
export const CYAN_B2F5EA = '#b9eee1'; // 3 occurrence(s), 1 file(s)
export const CYAN_00897B = '#1b6e59'; // 3 occurrence(s), 1 file(s)
export const CYAN_0CD6A8 = '#28ba5d'; // 2 occurrence(s), 2 file(s)
export const CYAN_00695C = '#155444'; // 2 occurrence(s), 2 file(s)
export const CYAN_DCECE9 = '#dcece2'; // 2 occurrence(s), 1 file(s)
export const CYAN_4DD0E1 = '#6b5ed0'; // 2 occurrence(s), 1 file(s)
export const CYAN_00BCD4 = '#3d30a4'; // 2 occurrence(s), 2 file(s)
export const CYAN_044034 = '#0c381c'; // 2 occurrence(s), 1 file(s)
export const CYAN_14B8A6 = '#29a385'; // 2 occurrence(s), 2 file(s)
export const CYAN_E0F7FA = '#e3e0fa'; // 1 occurrence(s), 1 file(s)
export const CYAN_00B09B = '#238d72'; // 1 occurrence(s), 1 file(s)
export const CYAN_B3F2FF = '#c8c3ee'; // 1 occurrence(s), 1 file(s)
export const CYAN_24C5A1 = '#29c060'; // 1 occurrence(s), 1 file(s)
export const CYAN_88D4CA = '#88d4c1'; // 1 occurrence(s), 1 file(s)
export const CYAN_0E7490 = '#2e247a'; // 1 occurrence(s), 1 file(s)
export const CYAN_155 = '#145242'; // 1 occurrence(s), 1 file(s)
export const CYAN_E0F7F5 = '#e0f7f1'; // 1 occurrence(s), 1 file(s)
export const CYAN_E0F2F1 = '#e0f2ee'; // 1 occurrence(s), 1 file(s)
export const CYAN_26A69A = '#29a385'; // 1 occurrence(s), 1 file(s)
export const CYAN_64FFDA_A5 = 'rgba(127, 228, 164, 0.05)'; // 1 occurrence(s), 1 file(s)
export const CYAN_68868F = 'rgb(115, 111, 136)'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Greens
// ---------------------------------------------------------------------------
export const GREEN_1AAF54 = '#23a653'; // 63 occurrence(s), 40 file(s)
export const GREEN_4CAF50 = '#4caf70'; // 55 occurrence(s), 34 file(s)
export const GREEN_065F46 = '#12532a'; // 46 occurrence(s), 22 file(s)
export const GREEN_10B981 = '#23a653'; // 46 occurrence(s), 37 file(s)
export const GREEN_D1FAE5 = '#d5f6e1'; // 42 occurrence(s), 32 file(s)
export const GREEN_166534 = '#166533'; // 40 occurrence(s), 14 file(s)
export const GREEN_DCFCE7 = '#e0f8e9'; // 32 occurrence(s), 21 file(s)
export const GREEN_059669 = '#1b8040'; // 31 occurrence(s), 17 file(s)
export const GREEN_22C55E = '#28bf5f'; // 31 occurrence(s), 24 file(s)
export const GREEN_48BB78 = '#48bb72'; // 23 occurrence(s), 8 file(s)
export const GREEN_38A169 = '#38a15f'; // 23 occurrence(s), 8 file(s)
export const GREEN_2E7D32 = '#2e7d4b'; // 14 occurrence(s), 8 file(s)
export const GREEN_168F45 = '#1d8844'; // 14 occurrence(s), 12 file(s)
export const GREEN_79AF5C = '#5caf7a'; // 11 occurrence(s), 1 file(s)
export const GREEN_16A34A = '#20994c'; // 8 occurrence(s), 5 file(s)
export const GREEN_D4EDDA = '#d4eddd'; // 7 occurrence(s), 4 file(s)
export const GREEN_155724 = '#15572d'; // 6 occurrence(s), 3 file(s)
export const GREEN_A5D6A7 = '#a5d6b7'; // 5 occurrence(s), 3 file(s)
export const GREEN_1B5E20 = '#1b5e34'; // 5 occurrence(s), 3 file(s)
export const GREEN_388E3C = '#388e58'; // 5 occurrence(s), 4 file(s)
export const GREEN_C8E6C9 = '#c8e6d3'; // 5 occurrence(s), 3 file(s)
export const GREEN_82CA9D = '#82ca9c'; // 4 occurrence(s), 1 file(s)
export const GREEN_047857 = '#166633'; // 3 occurrence(s), 3 file(s)
export const GREEN_81C784 = '#81c79b'; // 3 occurrence(s), 3 file(s)
export const GREEN_66BB6A = '#66bb85'; // 3 occurrence(s), 1 file(s)
export const GREEN_9DE3A4 = '#9de3b7'; // 3 occurrence(s), 1 file(s)
export const GREEN_C3E6CB = '#c3e6d0'; // 3 occurrence(s), 2 file(s)
export const GREEN_28A745 = '#28a757'; // 3 occurrence(s), 2 file(s)
export const GREEN_BBF7D0 = '#c0f2d2'; // 2 occurrence(s), 2 file(s)
export const GREEN_AED581 = '#81d5a0'; // 2 occurrence(s), 1 file(s)
export const GREEN_2ECC71 = '#2ecc68'; // 2 occurrence(s), 2 file(s)
export const GREEN_D6F5E3 = '#d6f5e1'; // 2 occurrence(s), 2 file(s)
export const GREEN_259725 = '#25974f'; // 2 occurrence(s), 2 file(s)
export const GREEN_06330F = '#0a2f18'; // 2 occurrence(s), 1 file(s)
export const GREEN_45A049 = '#45a066'; // 2 occurrence(s), 2 file(s)
export const GREEN_86EFAC = '#8ee7af'; // 2 occurrence(s), 2 file(s)
export const GREEN_99D799 = '#99d7b0'; // 2 occurrence(s), 2 file(s)
export const GREEN_0D8A3E = '#1a7d3e'; // 2 occurrence(s), 1 file(s)
export const GREEN_181 = '#1b7e3f'; // 2 occurrence(s), 1 file(s)
export const GREEN_189648 = '#1e9048'; // 2 occurrence(s), 1 file(s)
export const GREEN_174 = '#187038'; // 1 occurrence(s), 1 file(s)
export const GREEN_3CD500 = '#25b058'; // 1 occurrence(s), 1 file(s)
export const GREEN_96C93D = '#3dc970'; // 1 occurrence(s), 1 file(s)
export const GREEN_43A047 = '#43a065'; // 1 occurrence(s), 1 file(s)
export const GREEN_88DA2C = '#32d46e'; // 1 occurrence(s), 1 file(s)
export const GREEN_1E7145 = '#1e713c'; // 1 occurrence(s), 1 file(s)
export const GREEN_154F30 = '#154f2a'; // 1 occurrence(s), 1 file(s)
export const GREEN_DBFBCA = '#d0f5de'; // 1 occurrence(s), 1 file(s)
export const GREEN_166B1F = '#176a35'; // 1 occurrence(s), 1 file(s)
export const GREEN_D1FFD1 = '#d9f7e4'; // 1 occurrence(s), 1 file(s)
export const GREEN_5BC88E = '#5bc883'; // 1 occurrence(s), 1 file(s)
export const GREEN_158F45 = '#1d8744'; // 1 occurrence(s), 1 file(s)
export const GREEN_90EE90 = '#95e9b4'; // 1 occurrence(s), 1 file(s)
export const GREEN_006400 = '#125229'; // 1 occurrence(s), 1 file(s)
export const GREEN_E6F4EA = '#e6f4eb'; // 1 occurrence(s), 1 file(s)
export const GREEN_15803D = '#1a7b3e'; // 1 occurrence(s), 1 file(s)
export const GREEN_151 = '#12542a'; // 1 occurrence(s), 1 file(s)
export const GREEN_152 = '#12542a'; // 1 occurrence(s), 1 file(s)
export const GREEN_150 = '#0f4623'; // 1 occurrence(s), 1 file(s)
export const GREEN_159B49 = '#1f9149'; // 1 occurrence(s), 1 file(s)
export const GREEN_3DA817 = '#219e4f'; // 1 occurrence(s), 1 file(s)
export const GREEN_10B981_A30 = 'rgba(35, 166, 83, 0.3)'; // 9 occurrence(s), 8 file(s)
export const GREEN_1AAF54_A5 = 'rgba(35, 166, 83, 0.05)'; // 3 occurrence(s), 3 file(s)
export const GREEN_1AAF54_A2 = 'rgba(35, 166, 83, 0.02)'; // 3 occurrence(s), 3 file(s)
export const GREEN_10B981_A8 = 'rgba(35, 166, 83, 0.08)'; // 2 occurrence(s), 2 file(s)
export const GREEN_10B981_A20 = 'rgba(35, 166, 83, 0.2)'; // 2 occurrence(s), 2 file(s)
export const GREEN_10B981_A16 = 'rgba(35, 166, 83, 0.16)'; // 2 occurrence(s), 2 file(s)
export const GREEN_223318 = 'rgb(24, 51, 34)'; // 2 occurrence(s), 2 file(s)
export const GREEN_1AAF54_A4 = 'rgba(35, 166, 83, 0.04)'; // 1 occurrence(s), 1 file(s)
export const GREEN_44D600_A25 = 'rgba(37, 177, 88, 0.25)'; // 1 occurrence(s), 1 file(s)
export const GREEN_44D600_A35 = 'rgba(37, 177, 88, 0.35)'; // 1 occurrence(s), 1 file(s)
export const GREEN_4CAF50_A30 = 'rgba(76, 175, 112, 0.3)'; // 1 occurrence(s), 1 file(s)
export const GREEN_22C55E_A30 = 'rgba(40, 191, 95, 0.3)'; // 1 occurrence(s), 1 file(s)
export const GREEN_4CAF50_A8 = 'rgba(76, 175, 112, 0.08)'; // 1 occurrence(s), 1 file(s)
export const GREEN_1AAF54_A30 = 'rgba(35, 166, 83, 0.3)'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Ambers / yellows
// ---------------------------------------------------------------------------
export const AMBER_FEF3C7 = '#fcefc9'; // 60 occurrence(s), 31 file(s)
export const AMBER_FFF3CD = '#fdf1d0'; // 10 occurrence(s), 7 file(s)
export const AMBER_FEF9C3 = '#fceec5'; // 7 occurrence(s), 6 file(s)
export const AMBER_FAF9A7 = '#fae5a7'; // 5 occurrence(s), 1 file(s)
export const AMBER_EAB308 = '#e6af0c'; // 4 occurrence(s), 4 file(s)
export const AMBER_FFDA1F = '#f4c12a'; // 2 occurrence(s), 2 file(s)
export const AMBER_FFF5C7 = '#fcf0ca'; // 2 occurrence(s), 2 file(s)
export const AMBER_FFC107 = '#f3bb13'; // 2 occurrence(s), 2 file(s)
export const AMBER_FFF9C4 = '#fcefc7'; // 1 occurrence(s), 1 file(s)
export const AMBER_F0E68C = '#f0d78c'; // 1 occurrence(s), 1 file(s)
export const AMBER_A29415 = '#a27f15'; // 1 occurrence(s), 1 file(s)
export const AMBER_FCCF31 = '#f5c638'; // 1 occurrence(s), 1 file(s)
export const AMBER_F6D365 = '#f6d265'; // 1 occurrence(s), 1 file(s)
export const AMBER_FFF720 = '#f4c22b'; // 1 occurrence(s), 1 file(s)
export const AMBER_FCD34D = '#f6cd53'; // 1 occurrence(s), 1 file(s)
export const AMBER_FDE68A = '#f9de8e'; // 1 occurrence(s), 1 file(s)
export const AMBER_FFFF00 = '#f2b90d'; // 1 occurrence(s), 1 file(s)
export const AMBER_FFEAA7 = '#fbe7ab'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Oranges
// ---------------------------------------------------------------------------
export const ORANGE_92400E = '#924c0e'; // 47 occurrence(s), 16 file(s)
export const ORANGE_FF9800 = '#ec7813'; // 32 occurrence(s), 26 file(s)
export const ORANGE_F59E0B = '#ec7914'; // 30 occurrence(s), 21 file(s)
export const ORANGE_B45309 = '#af590e'; // 18 occurrence(s), 12 file(s)
export const ORANGE_D97706 = '#ce6911'; // 16 occurrence(s), 11 file(s)
export const ORANGE_A16207 = '#9b4f0d'; // 15 occurrence(s), 9 file(s)
export const ORANGE_F97316 = '#ed8122'; // 15 occurrence(s), 11 file(s)
export const ORANGE_7C4A03 = '#753c0a'; // 13 occurrence(s), 1 file(s)
export const ORANGE_E65100 = '#d56c11'; // 10 occurrence(s), 8 file(s)
export const ORANGE_F57C00 = '#e37412'; // 8 occurrence(s), 7 file(s)
export const ORANGE_ED6C02 = '#dd7112'; // 7 occurrence(s), 5 file(s)
export const ORANGE_856404 = '#7f410a'; // 7 occurrence(s), 4 file(s)
export const ORANGE_F57F17 = '#ed7f1f'; // 6 occurrence(s), 2 file(s)
export const ORANGE_795548 = '#795f48'; // 5 occurrence(s), 5 file(s)
export const ORANGE_FFA500 = '#ec7813'; // 4 occurrence(s), 2 file(s)
export const ORANGE_F5893F = '#f09444'; // 4 occurrence(s), 1 file(s)
export const ORANGE_CA9608 = '#c26310'; // 3 occurrence(s), 3 file(s)
export const ORANGE_E97F0B = '#e27312'; // 3 occurrence(s), 3 file(s)
export const ORANGE_F5D89A = '#f5c49a'; // 2 occurrence(s), 1 file(s)
export const ORANGE_FF9900 = '#ec7813'; // 2 occurrence(s), 2 file(s)
export const ORANGE_FF6B35 = '#f09444'; // 2 occurrence(s), 2 file(s)
export const ORANGE_E55A2B = '#e5822b'; // 2 occurrence(s), 2 file(s)
export const ORANGE_FFD3A5 = '#f8cfac'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FCE8B2 = '#f9d5b5'; // 1 occurrence(s), 1 file(s)
export const ORANGE_B7791F = '#b7661f'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FFA726 = '#ef8c36'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FFCC80 = '#f5bc8a'; // 1 occurrence(s), 1 file(s)
export const ORANGE_F2793D = '#ef9240'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FED7AA = '#f9d2af'; // 1 occurrence(s), 1 file(s)
export const ORANGE_C2410C = '#bf610f'; // 1 occurrence(s), 1 file(s)
export const ORANGE_CE8B54 = '#ce8d54'; // 1 occurrence(s), 1 file(s)
export const ORANGE_78350F = '#78400f'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FBBF24 = '#ee8931'; // 1 occurrence(s), 1 file(s)
export const ORANGE_8B4513 = '#8b4b13'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FCE4D6 = '#fce8d6'; // 1 occurrence(s), 1 file(s)
export const ORANGE_7F3300 = '#753c0a'; // 1 occurrence(s), 1 file(s)
export const ORANGE_F9A825 = '#ee8930'; // 1 occurrence(s), 1 file(s)
export const ORANGE_8A5300 = '#80410a'; // 1 occurrence(s), 1 file(s)
export const ORANGE_EF6C00 = '#dd7112'; // 1 occurrence(s), 1 file(s)
export const ORANGE_F0B429 = '#ee862b'; // 1 occurrence(s), 1 file(s)
export const ORANGE_7A5B00 = '#713a09'; // 1 occurrence(s), 1 file(s)
export const ORANGE_ED8936 = '#ed8b36'; // 1 occurrence(s), 1 file(s)
export const ORANGE_DD7724 = '#dd7a24'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FFA319_A25 = 'rgba(238, 133, 42, 0.25)'; // 1 occurrence(s), 1 file(s)
export const ORANGE_FFA319_A35 = 'rgba(238, 133, 42, 0.35)'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Reds
// ---------------------------------------------------------------------------
export const RED_991B1B = '#991b1b'; // 65 occurrence(s), 17 file(s)
export const RED_F44336 = '#e54646'; // 48 occurrence(s), 15 file(s)
export const RED_B91C1C = '#b91c1c'; // 46 occurrence(s), 16 file(s)
export const RED_DC2626 = '#dc2626'; // 45 occurrence(s), 22 file(s)
export const RED_D32F2F = '#d32f2f'; // 44 occurrence(s), 34 file(s)
export const RED_EF4444 = '#e64d4d'; // 42 occurrence(s), 27 file(s)
export const RED_EF381E = '#e12c2c'; // 13 occurrence(s), 1 file(s)
export const RED_E53E3E = '#e43f3f'; // 11 occurrence(s), 6 file(s)
export const RED_721C24 = '#721c1c'; // 9 occurrence(s), 3 file(s)
export const RED_F8D7DA = '#f8d7d7'; // 8 occurrence(s), 3 file(s)
export const RED_FED7D7 = '#fadbdb'; // 8 occurrence(s), 4 file(s)
export const RED_FEB2B2 = '#f5bbbb'; // 8 occurrence(s), 4 file(s)
export const RED_C62828 = '#c62828'; // 6 occurrence(s), 3 file(s)
export const RED_E31111 = '#d51f1f'; // 6 occurrence(s), 6 file(s)
export const RED_7F1D1D = '#7f1d1d'; // 6 occurrence(s), 6 file(s)
export const RED_F55555 = '#e96262'; // 5 occurrence(s), 3 file(s)
export const RED_FECACA = '#f8d0d0'; // 5 occurrence(s), 5 file(s)
export const RED_F4511E = '#e23131'; // 4 occurrence(s), 2 file(s)
export const RED_E53935 = '#e33838'; // 4 occurrence(s), 1 file(s)
export const RED_FF0000 = '#df2020'; // 4 occurrence(s), 4 file(s)
export const RED_FF5722 = '#e33e3e'; // 4 occurrence(s), 2 file(s)
export const RED_FCA5A5 = '#f3aeae'; // 3 occurrence(s), 2 file(s)
export const RED_B71C1C = '#b71c1c'; // 3 occurrence(s), 2 file(s)
export const RED_F5C6CB = '#f5c6c6'; // 3 occurrence(s), 2 file(s)
export const RED_EF9A9A = '#ef9a9a'; // 3 occurrence(s), 2 file(s)
export const RED_D33 = '#d33'; // 3 occurrence(s), 3 file(s)
export const RED_EF5350 = '#e75858'; // 2 occurrence(s), 2 file(s)
export const RED_FF7043 = '#e85a5a'; // 2 occurrence(s), 1 file(s)
export const RED_FFAB91 = '#f19f9f'; // 2 occurrence(s), 1 file(s)
export const RED_FF6B81 = '#ed7e7e'; // 2 occurrence(s), 2 file(s)
export const RED_D60404FF = '#bf1b1bff'; // 2 occurrence(s), 2 file(s)
export const RED_B80000FF = '#a11717ff'; // 2 occurrence(s), 2 file(s)
export const RED_DC3545 = '#dc3535'; // 2 occurrence(s), 1 file(s)
export const RED_7F1313 = '#7f1313'; // 2 occurrence(s), 1 file(s)
export const RED_DC0028 = '#c61651'; // 2 occurrence(s), 1 file(s)
export const RED_FD6585 = '#ef739c'; // 1 occurrence(s), 1 file(s)
export const RED_FDA085 = '#f09393'; // 1 occurrence(s), 1 file(s)
export const RED_F87171 = '#ec7d7d'; // 1 occurrence(s), 1 file(s)
export const RED_F0C7C2 = '#f0c2c2'; // 1 occurrence(s), 1 file(s)
export const RED_E57373 = '#e57373'; // 1 occurrence(s), 1 file(s)
export const RED_F29BA4 = '#f19c9c'; // 1 occurrence(s), 1 file(s)
export const RED_9A0007 = '#871313'; // 1 occurrence(s), 1 file(s)
export const RED_FFD1D1 = '#f9d7d7'; // 1 occurrence(s), 1 file(s)
export const RED_FFB6C1 = '#f6bfbf'; // 1 occurrence(s), 1 file(s)
export const RED_8B0000 = '#7a1111'; // 1 occurrence(s), 1 file(s)
export const RED_FADBD8 = '#fad9d9'; // 1 occurrence(s), 1 file(s)
export const RED_C53030 = '#c53030'; // 1 occurrence(s), 1 file(s)
export const RED_F56565 = '#eb7070'; // 1 occurrence(s), 1 file(s)
export const RED_FF8A65 = '#ec7878'; // 1 occurrence(s), 1 file(s)
export const RED_F01414 = 'rgb(224, 36, 36)'; // 5 occurrence(s), 5 file(s)
export const RED_D32F2F_A6 = 'rgba(211, 47, 47, 0.06)'; // 4 occurrence(s), 2 file(s)
export const RED_D32F2F_A8 = 'rgba(211, 47, 47, 0.08)'; // 4 occurrence(s), 2 file(s)
export const RED_D32F2F_A20 = 'rgba(211, 47, 47, 0.2)'; // 2 occurrence(s), 2 file(s)
export const RED_FF1943_A25 = 'rgba(232, 48, 109, 0.25)'; // 1 occurrence(s), 1 file(s)
export const RED_FF1943_A35 = 'rgba(232, 48, 109, 0.35)'; // 1 occurrence(s), 1 file(s)
export const RED_F44336_A30 = 'rgba(229, 70, 70, 0.3)'; // 1 occurrence(s), 1 file(s)
export const RED_EF4444_A30 = 'rgba(230, 77, 77, 0.3)'; // 1 occurrence(s), 1 file(s)
export const RED_D32F2F_A16 = 'rgba(211, 47, 47, 0.16)'; // 1 occurrence(s), 1 file(s)
export const RED_D32F2F_A4 = 'rgba(211, 47, 47, 0.04)'; // 1 occurrence(s), 1 file(s)
export const RED_F44336_A8 = 'rgba(229, 70, 70, 0.08)'; // 1 occurrence(s), 1 file(s)
export const RED_800000 = '#800000'; // maroon: the locked state of the Menu Settings lock icon (#353)

// ---------------------------------------------------------------------------
// Pinks / magentas
// ---------------------------------------------------------------------------
export const PINK_E91E63 = '#e62163'; // 12 occurrence(s), 10 file(s)
export const PINK_9C27B0 = '#5a2bac'; // 9 occurrence(s), 9 file(s)
export const PINK_DB2777 = '#db2763'; // 5 occurrence(s), 5 file(s)
export const PINK_F3E5F5 = '#ebe5f5'; // 4 occurrence(s), 4 file(s)
export const PINK_EC4899 = '#eb497f'; // 4 occurrence(s), 4 file(s)
export const PINK_F06292 = '#ee6492'; // 3 occurrence(s), 2 file(s)
export const PINK_AB47BC = '#7247bc'; // 2 occurrence(s), 1 file(s)
export const PINK_CE93D8 = '#ac93d8'; // 2 occurrence(s), 1 file(s)
export const PINK_FFD6E1 = '#fbdae5'; // 2 occurrence(s), 2 file(s)
export const PINK_880E4F = '#3f1e78'; // 1 occurrence(s), 1 file(s)
export const PINK_EA4C89 = '#ea4c81'; // 1 occurrence(s), 1 file(s)
export const PINK_F6CEEC = '#ddd1f3'; // 1 occurrence(s), 1 file(s)
export const PINK_D939CD = '#7642d0'; // 1 occurrence(s), 1 file(s)
export const PINK_F761A1 = '#ee6a96'; // 1 occurrence(s), 1 file(s)
export const PINK_86198F = '#472286'; // 1 occurrence(s), 1 file(s)
export const PINK_DDA0DD = '#b6a0dd'; // 1 occurrence(s), 1 file(s)
export const PINK_F3D2E4 = '#ded2f3'; // 1 occurrence(s), 1 file(s)
export const PINK_9C27B0_A5 = 'rgba(90, 43, 172, 0.05)'; // 1 occurrence(s), 1 file(s)
export const PINK_9C27B0_A8 = 'rgba(90, 43, 172, 0.08)'; // 1 occurrence(s), 1 file(s)

// ---------------------------------------------------------------------------
// Purples
// ---------------------------------------------------------------------------
export const PURPLE_8B5CF6 = '#857ad8'; // 17 occurrence(s), 15 file(s)
export const PURPLE_7C3AED = '#8253d4'; // 13 occurrence(s), 9 file(s)
export const PURPLE_6B21A8 = '#5428a1'; // 8 occurrence(s), 6 file(s)
export const PURPLE_9333EA = '#7c4bd2'; // 6 occurrence(s), 6 file(s)
export const PURPLE_764BA2 = '#6b4ba2'; // 5 occurrence(s), 3 file(s)
export const PURPLE_6A1B9A = '#4c2491'; // 4 occurrence(s), 3 file(s)
export const PURPLE_A855F7 = '#9871db'; // 4 occurrence(s), 4 file(s)
export const PURPLE_7C4DFF = '#8075d7'; // 4 occurrence(s), 1 file(s)
export const PURPLE_5B21B6 = '#5a2bac'; // 3 occurrence(s), 3 file(s)
export const PURPLE_A839E0 = '#7a48d1'; // 2 occurrence(s), 2 file(s)
export const PURPLE_A55EEA = '#956ddb'; // 2 occurrence(s), 2 file(s)
export const PURPLE_EAD6FF = '#e7def7'; // 2 occurrence(s), 2 file(s)
export const PURPLE_7B1FA2 = '#51279a'; // 2 occurrence(s), 2 file(s)
export const PURPLE_E9D5FF = '#e7ddf7'; // 2 occurrence(s), 1 file(s)
export const PURPLE_512DA8 = '#3e30a5'; // 1 occurrence(s), 1 file(s)
export const PURPLE_9708CC = '#592aaa'; // 1 occurrence(s), 1 file(s)
export const PURPLE_8C1BAB = '#53289e'; // 1 occurrence(s), 1 file(s)
export const PURPLE_673AB7 = '#493ab7'; // 1 occurrence(s), 1 file(s)
export const PURPLE_4B0082 = '#371a68'; // 1 occurrence(s), 1 file(s)
export const PURPLE_D8B4FE = '#d3c2f0'; // 1 occurrence(s), 1 file(s)
export const PURPLE_A78BFA = '#a9a1e4'; // 1 occurrence(s), 1 file(s)
export const PURPLE_B388FF = '#aaa3e4'; // 1 occurrence(s), 1 file(s)
export const PURPLE_6A3F8F = '#5c3f8f'; // 1 occurrence(s), 1 file(s)
export const PURPLE_8B5CF6_A8 = 'rgba(133, 122, 216, 0.08)'; // 2 occurrence(s), 1 file(s)
export const PURPLE_8B5CF6_A12 = 'rgba(133, 122, 216, 0.12)'; // 2 occurrence(s), 1 file(s)
export const PURPLE_8B5CF6_A20 = 'rgba(133, 122, 216, 0.2)'; // 2 occurrence(s), 1 file(s)
export const PURPLE_8B5CF6_A5 = 'rgba(133, 122, 216, 0.05)'; // 2 occurrence(s), 1 file(s)
export const PURPLE_8B5CF6_A30 = 'rgba(133, 122, 216, 0.3)'; // 2 occurrence(s), 1 file(s)
export const PURPLE_8B5CF6_A10 = 'rgba(133, 122, 216, 0.1)'; // 1 occurrence(s), 1 file(s)

// Recruitment module additions (no prior token matched these literals).
export const GREEN_14532D = '#14532b';
export const BLUE_3730A3 = '#3d30a3';
export const BLUE_C7D2FE = '#d2cdf8';
export const PURPLE_6D28D9 = '#6c35cc';
export const PURPLE_9D4EDD = '#8556d5';
export const PURPLE_C4B5FD = '#c3bdf6';
export const ORANGE_9A3412 = '#9a5112';
export const ORANGE_FFB74D = '#f2a15a';
export const RED_BE123C = '#bb154c';
export const NEAR_WHITE_F8F4FF = '#f6f5fe';

// ---------------------------------------------------------------------------
// Frozen aggregate export, for callers that prefer `COLORS.GREY_F5F5F5` over a
// named import. Both forms reference the same literals.
// ---------------------------------------------------------------------------
export const COLORS = Object.freeze({
  NEAR_WHITE_ECECE7,
  NEAR_WHITE_F7FAFC,
  NEAR_WHITE_F8FAFC,
  NEAR_WHITE_F5F5F5,
  NEAR_WHITE_FBFBF9,
  NEAR_WHITE_F1F5F9,
  NEAR_WHITE_FEE2E2,
  NEAR_WHITE_EEF1FF,
  NEAR_WHITE_F3F4F6,
  NEAR_WHITE_E3F2FD,
  NEAR_WHITE_FAFAF8,
  NEAR_WHITE_FAFAFA,
  NEAR_WHITE_F8F9FA,
  NEAR_WHITE_E0F2FE,
  NEAR_WHITE_FEF2F2,
  NEAR_WHITE_F9F9F9,
  NEAR_WHITE_F7F7F5,
  NEAR_WHITE_F0F0F0,
  NEAR_WHITE_F1F1F1,
  NEAR_WHITE_F0FDF4,
  NEAR_WHITE_EDF2F7,
  NEAR_WHITE_E8F5E9,
  NEAR_WHITE_FFEBEE,
  NEAR_WHITE_F9FAFB,
  NEAR_WHITE_EDE9FE,
  NEAR_WHITE_EFF6FF,
  NEAR_WHITE_FFFBEB,
  NEAR_WHITE_F3E8FF,
  NEAR_WHITE_E1F5FE,
  NEAR_WHITE_FFF3E0,
  NEAR_WHITE_F0F9FF,
  NEAR_WHITE_F1F2F4,
  NEAR_WHITE_FFF8E1,
  NEAR_WHITE_FFE6EB,
  NEAR_WHITE_EFF6FB,
  NEAR_WHITE_ECEFF1,
  NEAR_WHITE_EEE,
  NEAR_WHITE_E8F5E8,
  NEAR_WHITE_EEF2FF,
  NEAR_WHITE_F0F9EC,
  NEAR_WHITE_F2F2F2,
  NEAR_WHITE_F0F7FF,
  NEAR_WHITE_F8F8F6,
  NEAR_WHITE_FFF7ED,
  NEAR_WHITE_E6FFFA,
  NEAR_WHITE_E8EAFF,
  NEAR_WHITE_FFF4E6,
  NEAR_WHITE_E6F7FF,
  NEAR_WHITE_FCFCFA,
  NEAR_WHITE_EDE7F6,
  NEAR_WHITE_FCE4EC,
  NEAR_WHITE_F0F2F5,
  NEAR_WHITE_FBE9E7,
  NEAR_WHITE_E8F0FF,
  NEAR_WHITE_E8E8E8,
  NEAR_WHITE_FFF3F3,
  NEAR_WHITE_EBEBEB,
  NEAR_WHITE_F1F1EC,
  NEAR_WHITE_E6F3FF,
  NEAR_WHITE_ECFDF5,
  NEAR_WHITE_F0F4FF,
  NEAR_WHITE_F6F6F6,
  NEAR_WHITE_EEF0F2,
  NEAR_WHITE_FAFBFC,
  NEAR_WHITE_FCFDFE,
  NEAR_WHITE_E6F2FF,
  NEAR_WHITE_FFF1F0,
  NEAR_WHITE_F8FAFB,
  NEAR_WHITE_E5F7FF,
  NEAR_WHITE_E4E8FF,
  NEAR_WHITE_FAFAFB,
  NEAR_WHITE_FAFAFC,
  NEAR_WHITE_F0DEFD,
  NEAR_WHITE_DEF4FF,
  NEAR_WHITE_EDEDED,
  NEAR_WHITE_F8F8F8,
  NEAR_WHITE_FFE1E5,
  NEAR_WHITE_E1FFE9,
  NEAR_WHITE_F8F9FB,
  NEAR_WHITE_FFFDE7,
  NEAR_WHITE_FAE8FF,
  NEAR_WHITE_E0E7FF,
  NEAR_WHITE_FAFAF7,
  NEAR_WHITE_FFFCFC,
  NEAR_WHITE_FDECEA,
  NEAR_WHITE_FFF4E0,
  NEAR_WHITE_E8EEFC,
  NEAR_WHITE_F3E8FD,
  NEAR_WHITE_FDE8E8,
  NEAR_WHITE_FAF5FF,
  NEAR_WHITE_F5F3FF,
  NEAR_WHITE_FAFFFA,
  NEAR_WHITE_FFF5F5,
  NEAR_WHITE_FFFFFF_A50,
  NEAR_WHITE_FFFFFF,
  WHITE_FFFFFF,
  NEAR_WHITE_FFFFFF_A80,
  NEAR_WHITE_FFFFFF_A30,
  NEAR_WHITE_FFFFFF_A87,
  NEAR_WHITE_E5E8FF,
  NEAR_WHITE_FFFFFF_A20,
  NEAR_WHITE_FFFFFF_A70,
  NEAR_WHITE_FFFFFF_A40,
  NEAR_WHITE_FFFFFF_A10,
  NEAR_WHITE_FFFFFF_A95,
  NEAR_WHITE_F8FAFC_A30,
  NEAR_WHITE_FFFFFF_A90,
  NEAR_WHITE_FFFFFF_A30_2,
  NEAR_WHITE_FFFFFF_A18,
  NEAR_WHITE_FFFFFF_A7,
  NEAR_WHITE_FFFFFF_A15,
  NEAR_WHITE_FFFFFF_A55,
  NEAR_WHITE_FFFFFF_A18_2,
  NEAR_WHITE_FFFFFF_A14,
  NEAR_WHITE_FFFFFF_A85,
  NEAR_WHITE_FFFFFF_A70_2,
  GREY_6B6B72,
  GREY_9B9BA1,
  GREY_6B7280,
  GREY_2A2A2E,
  GREY_DCDCD5,
  GREY_333,
  GREY_E0E0E0,
  GREY_666,
  GREY_DDD,
  GREY_9CA3AF,
  GREY_757575,
  GREY_9E9E9E,
  GREY_CCC,
  GREY_7F7D7D,
  GREY_C1C1C1,
  GREY_A8A8A8,
  GREY_555,
  GREY_495057,
  GREY_999,
  GREY_E1DEDE91,
  GREY_818078,
  GREY_5C5454,
  GREY_5E5C5C,
  GREY_3A3A3F,
  GREY_C8CFD0,
  GREY_888,
  GREY_E2E3E5,
  GREY_868D9B,
  GREY_E1E1E1,
  GREY_BDBDBD,
  GREY_6A737D,
  GREY_5F6368,
  GREY_8A917F8C,
  GREY_33363B,
  GREY_C3C3C1,
  GREY_524C4D,
  GREY_D0D0D0,
  GREY_383D41,
  GREY_B0B0B0,
  GREY_7474774D,
  GREY_A2A2A2,
  GREY_ADB5BD,
  GREY_8C9196,
  GREY_434343,
  GREY_D6D6D6,
  GREY_3C4142,
  GREY_8E8E9C,
  GREY_6C757D,
  GREY_9C9992,
  GREY_BBB,
  GREY_AAA,
  GREY_D3D3D3,
  GREY_696969,
  GREY_6D6D74,
  GREY_616161,
  GREY_C7C7CC,
  GREY_D9D9DF66,
  GREY_333333,
  GREY_C2C1C1,
  GREY_E0E0E0_A100,
  GREY_474746,
  GREY_747070_A80,
  GREY_C6CDCB_A36,
  GREY_9CA3AF_A8,
  GREY_9CA3AF_A16,
  GREY_9CA3AF_A10,
  NEAR_BLACK_0A0A0B,
  NEAR_BLACK_000,
  NEAR_BLACK_000F1DFF,
  NEAR_BLACK_000000,
  NEAR_BLACK_111,
  NEAR_BLACK_222,
  NEAR_BLACK_1F1F22,
  NEAR_BLACK_1A1A1A,
  NEAR_BLACK_212529,
  NEAR_BLACK_000000_A6,
  NEAR_BLACK_000000_A10,
  NEAR_BLACK_000000_A10_2,
  NEAR_BLACK_000000_A8,
  NEAR_BLACK_000000_A15,
  NEAR_BLACK_000000_A5,
  NEAR_BLACK_0A0A0B_A4,
  NEAR_BLACK_000000_A20,
  NEAR_BLACK_000000_A12,
  NEAR_BLACK_000000_A4,
  NEAR_BLACK_000000_A4_2,
  NEAR_BLACK_0A0A0B_A4_2,
  NEAR_BLACK_0A0A0B_A3,
  NEAR_BLACK_000000_A23,
  NEAR_BLACK_0A0A0B_A3_2,
  NEAR_BLACK_000000_A42,
  NEAR_BLACK_000000_A30,
  NEAR_BLACK_0A0A0B_A6,
  NEAR_BLACK_000000_A2,
  NEAR_BLACK_000000_A60,
  NEAR_BLACK_000000_A40,
  NEAR_BLACK_0A0A0B_A6_2,
  NEAR_BLACK_000000_A22,
  NEAR_BLACK_000000_A5_2,
  NEAR_BLACK_000000_A10_3,
  NEAR_BLACK_000000_A35,
  NEAR_BLACK_0A0A0B_A12,
  NEAR_BLACK_0A0A0B_A18,
  NEAR_BLACK_000000_A8_2,
  NEAR_BLACK_000000_A25,
  NEAR_BLACK_0A0A0B_A32,
  NEAR_BLACK_0A0A0B_A5,
  NEAR_BLACK_000000_A2_2,
  NEAR_BLACK_000000_A90,
  NEAR_BLACK_000000_A60_2,
  NEAR_BLACK_000000_A45,
  NEAR_BLACK_070919_A18,
  NEAR_BLACK_070919_A15,
  NEAR_BLACK_000000_A12_2,
  NEAR_BLACK_000000_A54,
  NEAR_BLACK_0A0A0B_A35,
  NEAR_BLACK_000000_A2_3,
  NEAR_BLACK_14141E_A5,
  NEAR_BLACK_14141E_A3,
  NEAR_BLACK_000000_A7,
  NEAR_BLACK_0A0A0B_A18_2,
  NEAR_BLACK_000000_A50,
  NEAR_BLACK_000000_A55,
  NEAR_BLACK_000000_A6_2,
  NEAR_BLACK_000000_A24,
  NEAR_BLACK_000000_A14,
  BLUE_E2E8F0,
  BLUE_4A5568,
  BLUE_1F4FFF,
  BLUE_1976D2,
  BLUE_374151,
  BLUE_2D3748,
  BLUE_475569,
  BLUE_667EEA,
  BLUE_E5E7EB,
  BLUE_64748B,
  BLUE_5A6FD8,
  BLUE_1A202C,
  BLUE_D1D5DB,
  BLUE_00A8E8,
  BLUE_0EA5E9,
  BLUE_1E293B,
  BLUE_1E40AF,
  BLUE_DBEAFE,
  BLUE_81D4FA,
  BLUE_2196F3,
  BLUE_6366F1,
  BLUE_B3E5FC,
  BLUE_3B82F6,
  BLUE_94A3B8,
  BLUE_111827,
  BLUE_2980B9,
  BLUE_3F51B5,
  BLUE_42A5F5,
  BLUE_1565C0,
  BLUE_0277BD,
  BLUE_1D4ED8,
  BLUE_BBDEFB,
  BLUE_718096,
  BLUE_4F46E5,
  BLUE_223354B3,
  BLUE_334155,
  BLUE_CBD5E1,
  BLUE_4FC3F7,
  BLUE_9ED0F0,
  BLUE_607D8B,
  BLUE_29D,
  BLUE_1E3A8A,
  BLUE_90CAF9,
  BLUE_2563EB,
  BLUE_E9ECEF,
  BLUE_3D4ECC,
  BLUE_BFDBFE,
  BLUE_123,
  BLUE_125,
  BLUE_4481EB,
  BLUE_8884D8,
  BLUE_0B649C,
  BLUE_455A64,
  BLUE_2D3CA7,
  BLUE_0369A1,
  BLUE_1A3FCC,
  BLUE_0284C7,
  BLUE_1EA7E0,
  BLUE_007BFF,
  BLUE_1C81C2,
  BLUE_5C6AC0,
  BLUE_D0E2FF,
  BLUE_1ABCFE,
  BLUE_D6F0FF,
  BLUE_01579B,
  BLUE_115293,
  BLUE_17BCFF,
  BLUE_1740CC,
  BLUE_4338CA,
  BLUE_1E90FF,
  BLUE_8ECBEB,
  BLUE_637381,
  BLUE_3182CE,
  BLUE_BEE3F8,
  BLUE_A0AEC0,
  BLUE_1F2937,
  BLUE_93C5FD,
  BLUE_3B5BDB,
  BLUE_4C6EF5,
  BLUE_698FB7,
  BLUE_0077B5,
  BLUE_04BEFE,
  BLUE_1A1F36,
  BLUE_0070F3,
  BLUE_E0E4E8,
  BLUE_EAEDF0,
  BLUE_DEE2E6,
  BLUE_6B73FF,
  BLUE_000DFF,
  BLUE_ABDCFF,
  BLUE_0396FF,
  BLUE_141E30,
  BLUE_243B55,
  BLUE_2B5876,
  BLUE_4E4376,
  BLUE_97ABFF,
  BLUE_123597,
  BLUE_43CBFF,
  BLUE_29323C,
  BLUE_485563,
  BLUE_4B5563,
  BLUE_DFEBF6,
  BLUE_E9E9EE,
  BLUE_1C1C2E,
  BLUE_CFE2FF,
  BLUE_084298,
  BLUE_64B5F6,
  BLUE_1E88E5,
  BLUE_121826,
  BLUE_DDE1E7,
  BLUE_153BBB,
  BLUE_218,
  BLUE_D1E6FF,
  BLUE_039,
  BLUE_227,
  BLUE_87CEEB,
  BLUE_191970,
  BLUE_0C4A6E,
  BLUE_5966FF,
  BLUE_8ED4F3,
  BLUE_7DC0DF,
  BLUE_1A1A2E,
  BLUE_16213E,
  BLUE_0F3460,
  BLUE_157,
  BLUE_1A3FA0,
  BLUE_0D47A1,
  BLUE_127,
  BLUE_90A4AE,
  BLUE_4052CC,
  BLUE_5568D3,
  BLUE_223354_A10,
  BLUE_223354_A5,
  BLUE_223354_A70,
  BLUE_1976D2_A8,
  BLUE_0076FF_A39,
  BLUE_1976D2_A20,
  BLUE_1976D2_A4,
  BLUE_1976D2_A16,
  BLUE_26539C,
  BLUE_9FA2BF_A18,
  BLUE_9FA2BF_A32,
  BLUE_DAF4FF_A75,
  BLUE_9FA2BF_A18_2,
  BLUE_9FA2BF_A32_2,
  BLUE_1976D2_A10,
  BLUE_919EAB_A24,
  BLUE_1976D2_A30,
  BLUE_1F4FFF_A25,
  BLUE_6366F1_A30,
  BLUE_1F4FFF_A2,
  BLUE_2563EB_A25,
  BLUE_2563EB_A40,
  BLUE_4481EB_A8,
  BLUE_33C2FF_A25,
  BLUE_33C2FF_A35,
  BLUE_5569FF_A25,
  BLUE_5569FF_A35,
  BLUE_1F4FFF_A12,
  BLUE_2196F3_A10,
  BLUE_1F4FFF_A4,
  BLUE_5569FF_A15,
  BLUE_5569FF_A5,
  BLUE_1976D2_A12,
  BLUE_1F4FFF_A15,
  BLUE_1E293B_A100,
  BLUE_1976D2_A25,
  BLUE_919EAB_A12,
  BLUE_919EAB_A8,
  BLUE_6366F1_A40,
  BLUE_1E293B_A70,
  BLUE_1F4FFF_A20,
  BLUE_6366F1_A8,
  BLUE_6366F1_A16,
  BLUE_6366F1_A20,
  BLUE_223354_A50,
  BLUE_5569FF_A30,
  BLUE_5569FF_A40,
  BLUE_5569FF,
  BLUE_223354,
  CYAN_06B6D4,
  CYAN_155E75,
  CYAN_CFFAFE,
  CYAN_35CCE6,
  CYAN_0891B2,
  CYAN_B2EBF2,
  CYAN_0F766E,
  CYAN_D1ECF1,
  CYAN_0C5460,
  CYAN_009688,
  CYAN_64FFDA,
  CYAN_17A2B8,
  CYAN_38B2AC,
  CYAN_B2F5EA,
  CYAN_00897B,
  CYAN_0CD6A8,
  CYAN_00695C,
  CYAN_DCECE9,
  CYAN_4DD0E1,
  CYAN_00BCD4,
  CYAN_044034,
  CYAN_14B8A6,
  CYAN_E0F7FA,
  CYAN_00B09B,
  CYAN_B3F2FF,
  CYAN_24C5A1,
  CYAN_88D4CA,
  CYAN_0E7490,
  CYAN_155,
  CYAN_E0F7F5,
  CYAN_E0F2F1,
  CYAN_26A69A,
  CYAN_64FFDA_A5,
  CYAN_68868F,
  GREEN_1AAF54,
  GREEN_4CAF50,
  GREEN_065F46,
  GREEN_10B981,
  GREEN_D1FAE5,
  GREEN_166534,
  GREEN_DCFCE7,
  GREEN_059669,
  GREEN_22C55E,
  GREEN_48BB78,
  GREEN_38A169,
  GREEN_2E7D32,
  GREEN_168F45,
  GREEN_79AF5C,
  GREEN_16A34A,
  GREEN_D4EDDA,
  GREEN_155724,
  GREEN_A5D6A7,
  GREEN_1B5E20,
  GREEN_388E3C,
  GREEN_C8E6C9,
  GREEN_82CA9D,
  GREEN_047857,
  GREEN_81C784,
  GREEN_66BB6A,
  GREEN_9DE3A4,
  GREEN_C3E6CB,
  GREEN_28A745,
  GREEN_BBF7D0,
  GREEN_AED581,
  GREEN_2ECC71,
  GREEN_D6F5E3,
  GREEN_259725,
  GREEN_06330F,
  GREEN_45A049,
  GREEN_86EFAC,
  GREEN_99D799,
  GREEN_0D8A3E,
  GREEN_181,
  GREEN_189648,
  GREEN_174,
  GREEN_3CD500,
  GREEN_96C93D,
  GREEN_43A047,
  GREEN_88DA2C,
  GREEN_1E7145,
  GREEN_154F30,
  GREEN_DBFBCA,
  GREEN_166B1F,
  GREEN_D1FFD1,
  GREEN_5BC88E,
  GREEN_158F45,
  GREEN_90EE90,
  GREEN_006400,
  GREEN_E6F4EA,
  GREEN_15803D,
  GREEN_151,
  GREEN_152,
  GREEN_150,
  GREEN_159B49,
  GREEN_3DA817,
  GREEN_10B981_A30,
  GREEN_1AAF54_A5,
  GREEN_1AAF54_A2,
  GREEN_10B981_A8,
  GREEN_10B981_A20,
  GREEN_10B981_A16,
  GREEN_223318,
  GREEN_1AAF54_A4,
  GREEN_44D600_A25,
  GREEN_44D600_A35,
  GREEN_4CAF50_A30,
  GREEN_22C55E_A30,
  GREEN_4CAF50_A8,
  GREEN_1AAF54_A30,
  AMBER_FEF3C7,
  AMBER_FFF3CD,
  AMBER_FEF9C3,
  AMBER_FAF9A7,
  AMBER_EAB308,
  AMBER_FFDA1F,
  AMBER_FFF5C7,
  AMBER_FFC107,
  AMBER_FFF9C4,
  AMBER_F0E68C,
  AMBER_A29415,
  AMBER_FCCF31,
  AMBER_F6D365,
  AMBER_FFF720,
  AMBER_FCD34D,
  AMBER_FDE68A,
  AMBER_FFFF00,
  AMBER_FFEAA7,
  ORANGE_92400E,
  ORANGE_FF9800,
  ORANGE_F59E0B,
  ORANGE_B45309,
  ORANGE_D97706,
  ORANGE_A16207,
  ORANGE_F97316,
  ORANGE_7C4A03,
  ORANGE_E65100,
  ORANGE_F57C00,
  ORANGE_ED6C02,
  ORANGE_856404,
  ORANGE_F57F17,
  ORANGE_795548,
  ORANGE_FFA500,
  ORANGE_F5893F,
  ORANGE_CA9608,
  ORANGE_E97F0B,
  ORANGE_F5D89A,
  ORANGE_FF9900,
  ORANGE_FF6B35,
  ORANGE_E55A2B,
  ORANGE_FFD3A5,
  ORANGE_FCE8B2,
  ORANGE_B7791F,
  ORANGE_FFA726,
  ORANGE_FFCC80,
  ORANGE_F2793D,
  ORANGE_FED7AA,
  ORANGE_C2410C,
  ORANGE_CE8B54,
  ORANGE_78350F,
  ORANGE_FBBF24,
  ORANGE_8B4513,
  ORANGE_FCE4D6,
  ORANGE_7F3300,
  ORANGE_F9A825,
  ORANGE_8A5300,
  ORANGE_EF6C00,
  ORANGE_F0B429,
  ORANGE_7A5B00,
  ORANGE_ED8936,
  ORANGE_DD7724,
  ORANGE_FFA319_A25,
  ORANGE_FFA319_A35,
  RED_991B1B,
  RED_F44336,
  RED_B91C1C,
  RED_DC2626,
  RED_D32F2F,
  RED_EF4444,
  RED_EF381E,
  RED_E53E3E,
  RED_721C24,
  RED_F8D7DA,
  RED_FED7D7,
  RED_FEB2B2,
  RED_C62828,
  RED_E31111,
  RED_7F1D1D,
  RED_F55555,
  RED_FECACA,
  RED_F4511E,
  RED_E53935,
  RED_FF0000,
  RED_FF5722,
  RED_FCA5A5,
  RED_B71C1C,
  RED_F5C6CB,
  RED_EF9A9A,
  RED_D33,
  RED_EF5350,
  RED_FF7043,
  RED_FFAB91,
  RED_FF6B81,
  RED_D60404FF,
  RED_B80000FF,
  RED_DC3545,
  RED_7F1313,
  RED_DC0028,
  RED_FD6585,
  RED_FDA085,
  RED_F87171,
  RED_F0C7C2,
  RED_E57373,
  RED_F29BA4,
  RED_9A0007,
  RED_FFD1D1,
  RED_FFB6C1,
  RED_8B0000,
  RED_FADBD8,
  RED_C53030,
  RED_F56565,
  RED_FF8A65,
  RED_F01414,
  RED_D32F2F_A6,
  RED_D32F2F_A8,
  RED_D32F2F_A20,
  RED_FF1943_A25,
  RED_FF1943_A35,
  RED_F44336_A30,
  RED_EF4444_A30,
  RED_D32F2F_A16,
  RED_D32F2F_A4,
  RED_F44336_A8,
  RED_800000,
  PINK_E91E63,
  PINK_9C27B0,
  PINK_DB2777,
  PINK_F3E5F5,
  PINK_EC4899,
  PINK_F06292,
  PINK_AB47BC,
  PINK_CE93D8,
  PINK_FFD6E1,
  PINK_880E4F,
  PINK_EA4C89,
  PINK_F6CEEC,
  PINK_D939CD,
  PINK_F761A1,
  PINK_86198F,
  PINK_DDA0DD,
  PINK_F3D2E4,
  PINK_9C27B0_A5,
  PINK_9C27B0_A8,
  PURPLE_8B5CF6,
  PURPLE_7C3AED,
  PURPLE_6B21A8,
  PURPLE_9333EA,
  PURPLE_764BA2,
  PURPLE_6A1B9A,
  PURPLE_A855F7,
  PURPLE_7C4DFF,
  PURPLE_5B21B6,
  PURPLE_A839E0,
  PURPLE_A55EEA,
  PURPLE_EAD6FF,
  PURPLE_7B1FA2,
  PURPLE_E9D5FF,
  PURPLE_512DA8,
  PURPLE_9708CC,
  PURPLE_8C1BAB,
  PURPLE_673AB7,
  PURPLE_4B0082,
  PURPLE_D8B4FE,
  PURPLE_A78BFA,
  PURPLE_B388FF,
  PURPLE_6A3F8F,
  PURPLE_8B5CF6_A8,
  PURPLE_8B5CF6_A12,
  PURPLE_8B5CF6_A20,
  PURPLE_8B5CF6_A5,
  PURPLE_8B5CF6_A30,
  PURPLE_8B5CF6_A10,
  GREEN_14532D,
  BLUE_3730A3,
  BLUE_C7D2FE,
  PURPLE_6D28D9,
  PURPLE_9D4EDD,
  PURPLE_C4B5FD,
  ORANGE_9A3412,
  ORANGE_FFB74D,
  RED_BE123C,
  NEAR_WHITE_F8F4FF,
} as const);

export type ColorToken = keyof typeof COLORS;
